import RaceBarChart from './modules/chart.component.ts';
import { initShareButton } from './modules/share-component.ts';
import { SelectedCoinsComponent } from './modules/selected-coins.component.ts';
import { AvailableCoinsComponent } from './modules/available-coins.component.ts';
import { DateSelectionComponent } from './modules/date-selection.component.ts';
import { router } from './modules/router.ts';
import { parseCSVData, loadCoinHistory, loadAllCoinsData, coinsHistoricalDataMap } from './modules/data.loader.ts';
import type { QueryParams } from './modules/router.ts';

const MAX_SELECTABLE_COINS = 15;
const selectedCoins = new Set();
const topCoinIds = [];
const initialCoinsSelection = new Set();
const isDefaultRoute = router.getPathName() === '/' && !router.getQuery().coins;
const coinIdToCoinMap = new Map();
const chart = new RaceBarChart(document.getElementById('chart-container'), selectedCoins, coinsHistoricalDataMap);
const allCoins = [];
let selectedStartDate : Date;

// Update URL when coins selection changes
function updateUrlWithSelectedCoins(startDate?: string | null) {
    const selectedCoinsArray = Array.from(selectedCoins) as string[];
    const query: QueryParams = { coins: selectedCoinsArray };
    if (startDate) {
        query.startDate = startDate;
    }
    router.navigate('/', query);
}

const init = async () => {
    const {top_coins_history, coin_markets, latest_update, coinIdToCoinMap: cid2coinmap } = await loadAllCoinsData();
    cid2coinmap.forEach((coin, cid) => {
        coinIdToCoinMap.set(cid, coin);
    });

    allCoins.push(...coin_markets);
    topCoinIds.push(...Array.from(coinsHistoricalDataMap.keys()));

    const routeCoinIds = [(router.getQuery().coins || []) as string[]].flat();
    if (isDefaultRoute && routeCoinIds.length === 0) {
        topCoinIds.forEach(coinId => {
            selectedCoins.add(coinId);
            initialCoinsSelection.add(coinId);
        });
    } else {
        routeCoinIds.forEach(coinId => {
            selectedCoins.add(coinId);
            initialCoinsSelection.add(coinId);
        });
    }

    // Setup router
    router.on('/', async (params, query: QueryParams) => {
        let needsChartUpdate = false;

        // Handle coins parameter
        if (typeof query.coins === 'string') {
            // Handle single coin case
            const coinId = query.coins;
            if (coinIdToCoinMap.has(coinId)) {
                selectedCoins.clear();
                if (!coinsHistoricalDataMap.has(coinId)) {
                    coinsHistoricalDataMap.set(coinId, await loadCoinHistory(coinId));
                }
                selectedCoins.add(coinId);
                needsChartUpdate = true;
            }
        } else if (Array.isArray(query.coins)) {
            // Handle multiple coins case
            const coinIds = query.coins as string[];
            selectedCoins.clear();
            const toLoad = coinIds.filter(coinId => coinIdToCoinMap.has(coinId) && selectedCoins.size < MAX_SELECTABLE_COINS);
            const loaded = await Promise.all(toLoad.map(coinId => loadCoinHistory(coinId)));
            loaded.forEach(data => {
                coinsHistoricalDataMap.set(data.name, data);
            });
            coinIds.forEach(coinId => selectedCoins.add(coinId));
            needsChartUpdate = true;
        }

        // Handle startDate parameter
        if (query.startDate && typeof query.startDate === 'string') {
            chart.setStartDate(query.startDate);
            if (dateSelectionComponent) {
                dateSelectionComponent.setDate(query.startDate);
            }
        } else if (needsChartUpdate) {
            // Only reset the date if we're updating coins
            chart.setStartDate(null);
            if (dateSelectionComponent) {
                dateSelectionComponent.reset();
            }
        }

        // Update components if needed
        if (needsChartUpdate) {
            if (selectedCoinsComponent) selectedCoinsComponent.update();
            if (availableCoinsComponent) availableCoinsComponent.update();
        }
    });

    const toLoad = Array.from(selectedCoins).filter(coinId => !coinIdToCoinMap.has(coinId) && selectedCoins.size < MAX_SELECTABLE_COINS);
    const loaded = await Promise.all(toLoad.map(coinId => loadCoinHistory(coinId).then(csvData => parseCSVData(csvData, coinId, coinIdToCoinMap))));
    for (let i = 0; i < toLoad.length; i++ ) {
        coinsHistoricalDataMap.set(toLoad[i], loaded[i]);
    }
    chart.updateChart(false);
    
    // Display latest update information
    function displayLatestUpdate() {
        const lastUpdateSpan = document.querySelector('.last-update');
        if (lastUpdateSpan && latest_update) {
            const date = new Date(latest_update.lastUpdated);
            const formattedDate = date.toLocaleDateString(undefined, { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
            lastUpdateSpan.textContent = `Last updated: ${formattedDate}`;
        }
    }
    displayLatestUpdate();
    
    let selectedCoinsComponent, availableCoinsComponent, dateSelectionComponent;
    
    initShareButton(document.querySelector('.share-button'));
    const selectedContainer = document.querySelector('.coin-selected-container');
    const availableContainer = document.querySelector('.available-coins-container');
    const dateSelectionContainer = document.querySelector('.date-selection');
    
    if (!selectedContainer || !availableContainer) {
        throw new Error('Required DOM elements not found');
    }
    
    selectedCoinsComponent = new SelectedCoinsComponent(
        selectedContainer as HTMLElement,
        selectedCoins,
        coinIdToCoinMap,
        resetToInitialSelection,
        deselectCoin
    );
    availableCoinsComponent = new AvailableCoinsComponent(
        availableContainer as HTMLElement,
        selectedCoins,
        allCoins,
        addCoin
    );
    dateSelectionComponent = new DateSelectionComponent(
        dateSelectionContainer as HTMLElement,
        selectedCoins,
        coinIdToCoinMap,
        (date) => {
            chart.setStartDate(date);
            updateUrlWithSelectedCoins(date);
        }
    );

    function resetToInitialSelection() {
        selectedCoins.clear();
        initialCoinsSelection.forEach(coinId => {
            selectedCoins.add(coinId);
        });
        chart.updateChart(false);
        selectedCoinsComponent.update();
        availableCoinsComponent.update();
        if (isDefaultRoute) {
            router.navigate('/');
        } else {
            router.navigate('/', { coins: Array.from(initialCoinsSelection) });
        }
    }
    
    function deselectCoin(coinId) {
        selectedCoins.delete(coinId);
        chart.updateChart(false);
        selectedCoinsComponent.update();
        availableCoinsComponent.update();
        updateUrlWithSelectedCoins();
        dateSelectionComponent.update();
    }

    async function addCoin(coinId) {
        if (selectedCoins.size >= MAX_SELECTABLE_COINS) {
            return;
        }
        await loadCoinHistory(coinId)
        dateSelectionComponent.update();

        selectedCoins.add(coinId);
        chart.updateChart(false);
        selectedCoinsComponent.update();
        availableCoinsComponent.update();
        updateUrlWithSelectedCoins();
    }
    
    await chart.updateChart(false); // Start playing immediately without showing play button
};

document.addEventListener('DOMContentLoaded', async () => {
    await init().catch(console.error);
});
